"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LaneEvent = exports.LANE_CONDITION_EVENTS = exports.LaneEventType = exports.LANE_EVENT_REASSERT_SECONDS = exports.LANE_EVENT_TAG = void 0;
const zod_1 = require("zod");
/**
 * Lane health and incident events (RL-409).
 *
 * A lane can be broken for days with nothing reporting it: a dead fiscal
 * serial bridge, a fiscal register answering zero bytes for weeks, a terminal
 * unit that exited cleanly and stayed down, a duplicate instance holding the
 * scanner. Every one of those is trivially detectable on the lane. These
 * events are how the lane says so.
 *
 * Transport: an ordinary terminal log record (`SyncTerminalLogRecord`) with
 * `tag` = {@link LANE_EVENT_TAG} and the event in `context`. It rides the
 * existing `POST /sync/terminal-logs` outbox, so it is buffered on the lane
 * while BOS is unreachable and delivered, idempotently, when it is back
 * (TRS §11.7, §14.2: terminals do not push to Loki directly). BOS persists it
 * like any log line and re-emits accepted events as one structured log line,
 * which the platform log shipper labels and Loki alerts on.
 *
 * Labels are what the platform alerts group by, so the values that become
 * labels (`component`, `unit`, `target`, `peripheral`) are short slugs, never
 * free text, ids or counters. `tenant` and `terminal_id` are NEVER taken from
 * the lane: BOS sets them from the authenticated terminal.
 *
 * Telemetry privacy (TRS §14.5): operational metadata only. No receipt
 * content, no customer data, no cashier names.
 */
exports.LANE_EVENT_TAG = 'lane.event';
/**
 * Condition events are re-asserted by the lane while the condition holds, at
 * this cadence. The platform alerts treat "seen in the last 10 minutes" as
 * "still true", so a lane that stops re-asserting lets the alert resolve.
 */
exports.LANE_EVENT_REASSERT_SECONDS = 300;
exports.LaneEventType = zod_1.z.enum([
    // Service lifecycle — the terminal app and its companions (bridge, sidecar).
    'service.started',
    /** Clean exit (status 0). Two of these and a unit that stays down is a failure. */
    'service.exited',
    /** Non-zero exit or signal; `exitStatus` / `reason` where known. */
    'service.failed',
    /** Condition: a required companion unit is not running. Re-asserted. */
    'service.down',
    // Readiness (RL-405).
    'readiness.reached',
    /** Condition: readiness not reached; `waitingOn` says what. Re-asserted. */
    'readiness.timeout',
    // Peripherals.
    'peripheral.opened',
    'peripheral.first_data',
    /**
     * Condition: opened and has NEVER delivered a byte. The signature of a dead
     * fiscal register and of a mis-ported scanner. Re-asserted.
     */
    'peripheral.no_data',
    /** Condition: delivered data before, silent for `silentForSeconds`. Re-asserted. */
    'peripheral.silent',
    'peripheral.closed',
    // Upstream links (BOS, payment host, fiscal cloud).
    'upstream.connected',
    'upstream.disconnected',
    'upstream.refused',
    // Fiscal device (RL-406 taxonomy in `code`).
    'fiscal.online',
    /** Condition: device unreachable. Re-asserted. */
    'fiscal.offline',
    /** Condition: device answering with an error; `code` from the RL-406 taxonomy. Re-asserted. */
    'fiscal.error',
    // Inputs (RL-407).
    /** Inputs dropped while the lane was blocked; `count` of them. */
    'input.dropped',
]);
/** Events that describe an ongoing condition and are re-asserted while it holds. */
exports.LANE_CONDITION_EVENTS = [
    'service.down',
    'readiness.timeout',
    'peripheral.no_data',
    'peripheral.silent',
    'fiscal.offline',
    'fiscal.error',
];
/** A label-safe slug: lower-case, bounded, no spaces. */
const LabelSlug = zod_1.z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9_.@-]*$/, 'lower-case slug ([a-z0-9_.@-])');
exports.LaneEvent = zod_1.z.object({
    event: exports.LaneEventType,
    /** What emitted it: `terminal`, `fiscal-bridge`, `scanner`, `scale`, … */
    component: LabelSlug,
    /** systemd unit, e.g. `reform-terminal.service`. */
    unit: LabelSlug.optional(),
    /** Build / role target of the lane, e.g. `pos`, `sco`. BOS falls back to the terminal kind. */
    target: LabelSlug.optional(),
    /** Peripheral id for `peripheral.*` events, e.g. `fiscal-printer`, `scanner-1`. */
    peripheral: LabelSlug.optional(),
    /** Upstream id for `upstream.*` events, e.g. `bos`, `payment-host`. */
    upstream: LabelSlug.optional(),
    /** Why, where known: exit reason, refusal reason. Short, human-readable. */
    reason: zod_1.z.string().min(1).max(500).optional(),
    /** Process exit status for `service.exited` / `service.failed`. */
    exitStatus: zod_1.z.number().int().optional(),
    /** What readiness was waiting on for `readiness.timeout` (RL-405). */
    waitingOn: zod_1.z.string().min(1).max(200).optional(),
    /** Fiscal error taxonomy code (RL-406). */
    code: zod_1.z.string().min(1).max(64).optional(),
    /** Count of dropped inputs for `input.dropped` (RL-407). */
    count: zod_1.z.number().int().nonnegative().optional(),
    /** Seconds the peripheral has been silent / the condition has held. */
    silentForSeconds: zod_1.z.number().int().nonnegative().optional(),
});
//# sourceMappingURL=lane-events.js.map